import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { weekDays, scheduleTypes, cycleTypes } from "./scheduleManagerConstants";

const ScheduleFormDialog = ({
  open,
  onOpenChange,
  editingScheduleId,
  scheduleForm,
  setScheduleForm,
  handleSaveSchedule,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingScheduleId ? t("planning.scheduleManager.form.editTitle") : t("planning.scheduleManager.form.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("planning.scheduleManager.form.description")}
          </DialogDescription>
        </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{t("planning.scheduleManager.form.name")}</Label>
          <Input
            value={scheduleForm.name}
            onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
            placeholder={t("planning.scheduleManager.form.namePlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("planning.scheduleManager.form.descriptionLabel")}</Label>
          <Textarea
            rows={3}
            value={scheduleForm.description}
            onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("planning.scheduleManager.form.scheduleType")}</Label>
            <Select
              value={scheduleForm.schedule_type}
              onValueChange={(value) => setScheduleForm({ ...scheduleForm, schedule_type: value })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {scheduleTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{t(type.labelKey)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("planning.scheduleManager.form.cycle")}</Label>
            <Select
              value={scheduleForm.cycle_type}
              onValueChange={(value) => setScheduleForm({ ...scheduleForm, cycle_type: value })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {cycleTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{t(type.labelKey)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">{t("planning.scheduleManager.form.autoCreate")}</Label>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">{t("planning.scheduleManager.form.enable")}</Label>
              <button
                type="button"
                onClick={() =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    default_schedule_enabled: !prev.default_schedule_enabled,
                  }))
                }
                className={`w-10 h-5 rounded-full flex items-center px-1 transition ${
                  scheduleForm.default_schedule_enabled ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`w-4 h-4 bg-card dark:bg-gray-200 rounded-full shadow transform transition ${
                    scheduleForm.default_schedule_enabled ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("planning.scheduleManager.form.autoCreateHelp")}
          </p>

          {scheduleForm.default_schedule_enabled && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm">{t("planning.scheduleManager.form.workingDays")}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {weekDays.map((day) => {
                    const selected = scheduleForm.default_days.includes(day.value);
                    return (
                      <button
                        type="button"
                        key={day.value}
                        onClick={() => {
                          setScheduleForm((prev) => {
                            const exists = prev.default_days.includes(day.value);
                            return {
                              ...prev,
                              default_days: exists
                                ? prev.default_days.filter((v) => v !== day.value)
                                : [...prev.default_days, day.value],
                            };
                          });
                        }}
                        className={`border rounded px-2 py-1 text-sm transition ${
                          selected
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-border text-muted-foreground"
                        }`}
                      >
                        {t(day.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("planning.scheduleManager.form.startTime")}</Label>
                  <Input
                    type="time"
                    value={scheduleForm.default_start_time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, default_start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("planning.scheduleManager.form.endTime")}</Label>
                  <Input
                    type="time"
                    value={scheduleForm.default_end_time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, default_end_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("planning.scheduleManager.form.breakStart")}</Label>
                  <Input
                    type="time"
                    value={scheduleForm.default_break_start}
                    disabled={!scheduleForm.default_break_enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, default_break_start: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("planning.scheduleManager.form.breakEnd")}</Label>
                  <Input
                    type="time"
                    value={scheduleForm.default_break_end}
                    disabled={!scheduleForm.default_break_enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, default_break_end: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm">{t("planning.scheduleManager.form.includeBreak")}</Label>
                <button
                  type="button"
                  onClick={() =>
                    setScheduleForm((prev) => ({
                      ...prev,
                      default_break_enabled: !prev.default_break_enabled,
                    }))
                  }
                  className={`w-10 h-5 rounded-full flex items-center px-1 transition ${
                    scheduleForm.default_break_enabled ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`w-4 h-4 bg-card dark:bg-gray-200 rounded-full shadow transform transition ${
                      scheduleForm.default_break_enabled ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("planning.scheduleManager.form.hoursPerDay")}</Label>
            <Input
              type="number"
              value={scheduleForm.hours_per_day}
              onChange={(e) => setScheduleForm({ ...scheduleForm, hours_per_day: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("planning.scheduleManager.form.hoursPerWeek")}</Label>
            <Input
              type="number"
              value={scheduleForm.hours_per_week}
              onChange={(e) => setScheduleForm({ ...scheduleForm, hours_per_week: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("planning.scheduleManager.form.lateTolerance")}</Label>
            <Input
              type="number"
              value={scheduleForm.late_arrival_tolerance_minutes}
              onChange={(e) =>
                setScheduleForm({ ...scheduleForm, late_arrival_tolerance_minutes: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{t("planning.scheduleManager.form.earlyTolerance")}</Label>
            <Input
              type="number"
              value={scheduleForm.early_departure_tolerance_minutes}
              onChange={(e) =>
                setScheduleForm({ ...scheduleForm, early_departure_tolerance_minutes: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("planning.scheduleManager.actions.cancel")}</Button>
        <Button onClick={handleSaveSchedule}>
          {editingScheduleId ? t("planning.scheduleManager.actions.save") : t("planning.scheduleManager.actions.create")}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default ScheduleFormDialog;
